/**
 * computeMovedTimes — pure helper that produces the new startTime/finishTime
 * for an event being dragged from one day cell to another in Month View.
 *
 * The contract mirrors the legacy presenter behaviour
 * (`source/js/presenters/calendar-month.js:147`):
 *
 *     daysShift = (toDayTs - fromDayTs)        // in seconds
 *     newStart  = originalStart + daysShift
 *     newFinish = originalFinish + daysShift
 *
 * That preserves the time-of-day across the move (drag a 9 AM flight from
 * Apr 8 → Apr 10 and it stays at 9 AM, just two days later).
 *
 * All inputs are unix-second timestamps (numbers OR string-of-number — both
 * tolerated to match the legacy payload shapes). Returns `null` for any
 * invalid input rather than throwing, so callers can guard with a single
 * truthy check.
 *
 * @module computeMovedTimes
 */

const toSeconds = value => {
	if (value === null || value === undefined) return null;
	const n = typeof value === 'number' ? value : parseInt(value, 10);
	return Number.isFinite(n) ? n : null;
};

/**
 * @param {Object} event Event with `startTime`, `finishTime`.
 * @param {number|string} fromDayTs Source day's `unixTimestamp`.
 * @param {number|string} toDayTs Destination day's `unixTimestamp`.
 * @returns {{startTime: number, finishTime: number, daysShift: number}|null}
 */
export function computeMovedTimes(event, fromDayTs, toDayTs) {
	if (!event) return null;
	const start = toSeconds(event.startTime);
	const finish = toSeconds(event.finishTime);
	const fromTs = toSeconds(fromDayTs);
	const toTs = toSeconds(toDayTs);
	if (start == null || fromTs == null || toTs == null) return null;
	const daysShift = toTs - fromTs;
	if (daysShift === 0) return null; // same day → no-op
	return {
		startTime: start + daysShift,
		finishTime: finish == null ? start + daysShift : finish + daysShift,
		daysShift
	};
}

/**
 * Build the request payload that `CalendarApi.moveEvent` /
 * `CalendarApi.moveMyEvent` expects (legacy parity:
 * `source/js/factories/event.js:189`).
 *
 * @param {Object} times Result of `computeMovedTimes`.
 * @param {{timezone?: string}} [options]
 */
export function buildMovePayload(times, options = {}) {
	if (!times) return null;
	const { timezone, organizationUnique } = options;
	const payload = {
		startTime: times.startTime,
		finishTime: times.finishTime
	};
	if (timezone) payload.timezone = timezone;
	if (organizationUnique) payload.organizationUnique = organizationUnique;
	return payload;
}
